function readProperty(value: unknown, key: string): unknown {
  if ((typeof value !== "object" && typeof value !== "function") || value === null)
    return undefined;
  try {
    return Reflect.get(value, key);
  } catch {
    return undefined;
  }
}

export function databricksErrorStatusCode(error: unknown): number | undefined {
  for (const key of ["httpStatusCode", "statusCode", "status"]) {
    const value = readProperty(error, key);
    if (typeof value === "number" && Number.isInteger(value) && value >= 100 && value <= 599)
      return value;
    if (typeof value === "string" && /^\d{3}$/u.test(value)) return Number(value);
  }
  return undefined;
}

/** Preserve actionable SDK error metadata even when ApiError.message is empty. */
export function databricksErrorMessage(error: unknown): string {
  const message = error instanceof Error ? error.message.trim() : "";
  const status = databricksErrorStatusCode(error);
  const codeValue = readProperty(error, "code") ?? readProperty(error, "errorCode");
  const code = typeof codeValue === "string" && codeValue.trim() ? codeValue.trim() : undefined;
  const body = decodeHttpBody(readProperty(error, "httpBody"));
  const metadata = [status !== undefined ? `HTTP ${status}` : undefined, code]
    .filter((value): value is string => Boolean(value))
    .join("; ");

  if (message) return metadata ? `${message} (${metadata})` : message;
  if (body) return metadata ? `Databricks API error (${metadata}): ${body}` : body;
  if (metadata) return `Databricks API error (${metadata})`;

  const fallback = String(error);
  return fallback && fallback !== "[object Object]" ? fallback : "Unknown Databricks SDK error.";
}

function decodeHttpBody(value: unknown): string | undefined {
  if (!(value instanceof Uint8Array) || value.byteLength === 0) return undefined;
  const decoded = new TextDecoder().decode(value).trim();
  if (!decoded) return undefined;
  return decoded.length > 4_096 ? `${decoded.slice(0, 4_096)}…` : decoded;
}
